import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { InstrumentSparklineData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { TooltipSparkline } from './TooltipSparkline'

const TREND_COLOR_POSITIVE = '#6B8CAE'
const TREND_COLOR_NEGATIVE = '#D4A574'
const TOOLTIP_BORDER = '#2a2a2a'
const MUTED_TEXT = '#888888'
const SECONDARY_MUTED = '#666666'

const SparklineSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.5rem;
`

const LoadingText = styled.span`
  font-size: 0.75rem;
  color: ${MUTED_TEXT};
  font-style: italic;
`

const NoDataMessage = styled.div`
  font-size: 0.75rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  padding: 0.75rem 0.5rem;
`

const DatapointCount = styled.div`
  font-size: 0.625rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  padding-top: 0.25rem;
`

const ActionsHint = styled.div`
  font-size: 0.625rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${TOOLTIP_BORDER};
`

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.3rem;
  padding-top: 0.125rem;
`

const CarouselDot = styled.span<{ $active: boolean }>`
  width: 0.3rem;
  height: 0.3rem;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? MUTED_TEXT : TOOLTIP_BORDER)};
`

const CAROUSEL_INTERVAL_MS = 1000
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const SLIDE_DURATION_MS = 320

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`

/**
 * Clips the slide without reserving space of its own: the incoming pane stays
 * in normal flow and sets the height, so the tooltip is exactly as tall as the
 * single sparkline it was already showing.
 */
const CarouselViewport = styled.div`
  position: relative;
  overflow: hidden;
`

type PanePhase = 'static' | 'incoming' | 'outgoing'

const CarouselPane = styled.div<{ $phase: PanePhase }>`
  ${({ $phase }) => {
    if ($phase === 'outgoing') {
      return css`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        animation: ${slideOut} ${SLIDE_DURATION_MS}ms ease-in-out both;
      `
    }
    if ($phase === 'incoming') {
      return css`
        animation: ${slideIn} ${SLIDE_DURATION_MS}ms ease-in-out both;
      `
    }
    return null
  }}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    ${({ $phase }) =>
      $phase === 'outgoing' &&
      css`
        display: none;
      `}
  }
`

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches

function AnimatedLoadingText({ message }: { message: string }) {
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 400)
    return () => clearInterval(interval)
  }, [])

  return <LoadingText>{message + dots}</LoadingText>
}

interface SparklineTooltipContentProps {
  sparklineData: InstrumentSparklineData[]
  isLoading: boolean
  datapointCount: number
  error?: boolean
  animateLoading?: boolean
  actionsHint?: React.ReactNode
  showLastMeasured?: boolean
  loadingMessage?: string
  /**
   * Show one sparkline at a time, sliding to the next every second with
   * wrap-around (hover tooltips). Reduced-motion users get the first
   * sparkline, static.
   */
  carousel?: boolean
  /**
   * How many series the host renders/fetches at most (default 3). The
   * "+N more datapoint(s)" line appears only past this bound.
   */
  maxSeries?: number
  datapointIdToDescription?: ReadonlyMap<string, string>
}

export function SparklineTooltipContent({
  sparklineData,
  isLoading,
  datapointCount,
  error = false,
  animateLoading = false,
  actionsHint,
  showLastMeasured = true,
  loadingMessage,
  carousel = false,
  maxSeries = 3,
  datapointIdToDescription,
}: SparklineTooltipContentProps) {
  const { t } = useLingui()
  const resolvedLoadingMessage = loadingMessage
    ? loadingMessage
    : animateLoading
      ? t`Fetching live data`
      : t`Loading sensor data...`
  const extraDatapointCount = datapointCount - maxSeries

  const sparklineCount = sparklineData.length
  const cycling = carousel && sparklineCount > 1
  const [slide, setSlide] = useState<{
    index: number
    previous: number | null
  }>({ index: 0, previous: null })

  useEffect(() => {
    setSlide({ index: 0, previous: null })
    if (!cycling || prefersReducedMotion()) return
    const interval = setInterval(
      () =>
        setSlide((prev) => ({
          index: (prev.index + 1) % sparklineCount,
          previous: prev.index % sparklineCount,
        })),
      CAROUSEL_INTERVAL_MS
    )
    return () => clearInterval(interval)
  }, [cycling, sparklineCount])

  const activeIndex = cycling ? slide.index % sparklineCount : 0
  const previousIndex =
    cycling && slide.previous !== null ? slide.previous % sparklineCount : null

  const renderSparkline = (data: InstrumentSparklineData) => {
    const isPositiveTrend = data.currentValue >= data.previousValue
    const trendColor = isPositiveTrend
      ? TREND_COLOR_POSITIVE
      : TREND_COLOR_NEGATIVE
    return (
      <TooltipSparkline
        key={data.id}
        piTag={data.piTag}
        description={datapointIdToDescription?.get(data.id)}
        data={data.data}
        currentValue={data.currentValue}
        previousValue={data.previousValue}
        lastMeasuredAt={data.lastMeasuredAt}
        unit={data.unit}
        color={trendColor}
        showLastMeasured={showLastMeasured}
      />
    )
  }

  // Keyed by series id so a sparkline that moves from the incoming slot to the
  // outgoing slot keeps its chart instance instead of tearing down and rebuilding.
  const carouselPanes: { data: InstrumentSparklineData; phase: PanePhase }[] =
    previousIndex === null || previousIndex === activeIndex
      ? [{ data: sparklineData[activeIndex], phase: 'static' }]
      : [
          { data: sparklineData[previousIndex], phase: 'outgoing' },
          { data: sparklineData[activeIndex], phase: 'incoming' },
        ]

  return (
    <>
      <SparklineSection>
        {isLoading ? (
          <LoadingContainer>
            {animateLoading ? (
              <AnimatedLoadingText message={resolvedLoadingMessage} />
            ) : (
              <LoadingText>{resolvedLoadingMessage}</LoadingText>
            )}
          </LoadingContainer>
        ) : error ? (
          <NoDataMessage>{t`Unable to load time series data`}</NoDataMessage>
        ) : sparklineData.length === 0 ? (
          <NoDataMessage>{t`No recent data available`}</NoDataMessage>
        ) : cycling ? (
          <CarouselViewport>
            {carouselPanes.map(({ data, phase }) => (
              <CarouselPane
                key={data.id}
                $phase={phase}
                data-carousel-pane={phase}
                aria-hidden={phase === 'outgoing'}>
                {renderSparkline(data)}
              </CarouselPane>
            ))}
          </CarouselViewport>
        ) : (
          sparklineData.map((data) => renderSparkline(data))
        )}
        {cycling && (
          <CarouselDots aria-hidden>
            {sparklineData.map((data, index) => (
              <CarouselDot
                key={data.id}
                data-carousel-dot
                data-active={index === activeIndex}
                $active={index === activeIndex}
              />
            ))}
          </CarouselDots>
        )}
      </SparklineSection>

      {extraDatapointCount > 0 && (
        <DatapointCount>
          {plural(extraDatapointCount, {
            one: '+# more datapoint',
            other: '+# more datapoints',
          })}
        </DatapointCount>
      )}

      {actionsHint && <ActionsHint>{actionsHint}</ActionsHint>}
    </>
  )
}
